import { zoneCountries } from "./ZoneScope.js";

/**
 * Catalogue des modes REPRENABLES, c'est-à-dire ceux qui laissent une partie en
 * cours dans le localStorage : la page /parties-en-cours en dérive sa liste.
 *
 * Distinct de `GAME_GROUPS` (`gameGroups.js`), qui décrit les modes CLASSÉS — les
 * deux listes ne se recouvrent pas : le Speed Run est classé mais ne se reprend
 * pas (60 s d'un bloc, aucun état persisté), les zones continent de Map Nerd se
 * reprennent mais ne sont jamais classées, et Flag Nerd Tricky se reprend sans
 * être classé. Les fusionner reviendrait à mentir dans l'une des deux vues.
 *
 * `kind` dit où lire l'état :
 *   - `pool`    : jeux à vies (BaseGame) — `{gameId}.countriesleft` + `.lifes`
 *   - `letters` : Country Nerd — une partie par lettre commencée
 *   - `daily`   : Défi du jour — `geonerd.daily[.hard].progress`
 */

/** Zones de Map Nerd, alignées sur `MapNerdController.CONTINENTS`. */
export const MAP_CONTINENTS = ["af", "eu", "as", "am"];

const mapScopes = (base, slug, modeKey) => [
    { gameId: base, gameNameKey: "home.mapNerd", modeKey, slug, kind: "pool", pool: "easy" },
    ...MAP_CONTINENTS.map((code) => ({
        gameId: `${base}.${code}`,
        gameNameKey: "home.mapNerd",
        modeKey,
        // La zone n'a pas d'URL propre : le sélecteur de zone est sur la page du mode.
        slug,
        kind: "pool",
        pool: "easy",
        continent: code,
        scopeKey: `mapExploration.continents.${code}`,
    })),
];

/** Flag Nerd et Capital Nerd Classic par continent (`?zone=af`, voir ZoneScope.js). */
const quizZones = (base, gameNameKey, slug) => MAP_CONTINENTS.map((code) => ({
    gameId: `${base}.${code}`,
    gameNameKey,
    modeKey: "home.classic",
    slug,
    query: `?zone=${code}`,
    kind: "pool",
    pool: "countries",
    zone: code,
    scopeKey: `mapExploration.continents.${code}`,
}));

export const RESUMABLE_MODES = [
    { gameId: "dailychallenge",     gameNameKey: "home.dailyChallenge", modeKey: "home.classic", slug: "daily-challenge",      kind: "daily", mode: "classic", total: 20 },
    { gameId: "dailychallengehard", gameNameKey: "home.dailyChallenge", modeKey: "home.hard",    slug: "daily-challenge-hard", kind: "daily", mode: "hard",    total: 20 },

    { gameId: "flagnerd",       gameNameKey: "home.flagNerd", modeKey: "home.classic", slug: "flag-nerd-classic", kind: "pool", pool: "countries" },
    { gameId: "flagnerdhard",   gameNameKey: "home.flagNerd", modeKey: "home.hard",    slug: "flag-nerd-hard",    kind: "pool", pool: "countries" },
    { gameId: "flagnerdtricky", gameNameKey: "home.flagNerd", modeKey: "home.tricky",  slug: "flag-nerd-tricky",  kind: "pool", pool: "countries" },
    ...quizZones("flagnerd", "home.flagNerd", "flag-nerd-classic"),

    ...mapScopes("mapnerdclassic", "map-nerd-classic", "home.classic"),
    ...mapScopes("mapnerdhard", "map-nerd-hard", "home.hard"),

    { gameId: "capitalnerd",        gameNameKey: "home.capitalNerd", modeKey: "home.classic", slug: "capital-nerd-classic", kind: "pool", pool: "countries" },
    { gameId: "capitalnerdhard",    gameNameKey: "home.capitalNerd", modeKey: "home.hard",    slug: "capital-nerd-hard",    kind: "pool", pool: "countries" },
    { gameId: "capitalnerdreverse", gameNameKey: "home.capitalNerd", modeKey: "home.reverse", slug: "capital-nerd-reverse", kind: "pool", pool: "countries" },
    ...quizZones("capitalnerd", "home.capitalNerd", "capital-nerd-classic"),

    { gameId: "countrynerd",     gameNameKey: "home.countryNerd", modeKey: "home.classic", slug: "country-nerd",      kind: "letters" },
    { gameId: "countrynerdhard", gameNameKey: "home.countryNerd", modeKey: "home.hard",    slug: "country-nerd-hard", kind: "letters" },

    // Le Lab (Border Nerd n'y figure pas : son pool dépend de données chargées
    // sur sa seule page, le total affiché serait faux).
    { gameId: "shapenerd", gameNameKey: "lab.shapeNerd", modeKey: "home.classic", slug: "shape-nerd", kind: "pool", pool: "easy" },
    { gameId: "shapenerdhard", gameNameKey: "lab.shapeNerd", modeKey: "home.hard", slug: "shape-nerd-hard", kind: "pool", pool: "easy" },

    { gameId: "usstatesclassic", gameNameKey: "home.usStates", modeKey: "home.classic", slug: "us-states-classic", kind: "pool", pool: "usStates" },
    { gameId: "usstateshard",    gameNameKey: "home.usStates", modeKey: "home.hard",    slug: "us-states-hard",    kind: "pool", pool: "usStates" },
    // Pas de vies (voir UsStatesList.js) : `noLives` évite à InProgress.js d'afficher
    // à tort un compte de cœurs pour ce mode.
    { gameId: "usstateslist",    gameNameKey: "home.usStates", modeKey: "home.list",    slug: "us-states-list",    kind: "pool", pool: "usStates", noLives: true },
];

/**
 * Nombre de questions total d'un mode, au moment où on le lit : les données de
 * pays sont chargées à l'exécution, jamais codées en dur (le jour où un État
 * entre à l'ONU, un total figé mentirait sur la progression).
 */
export function poolSize(mode, app) {
    if (mode.continent) return (app.easyCountriesByContinent?.[mode.continent] || []).length;
    if (mode.zone) return zoneCountries(app.countries || [], mode.zone).length;
    if (mode.pool === "easy") return (app.easyCountries || []).length;
    if (mode.pool === "usStates") return (app.usStates || []).length;
    return (app.countries || []).length;
}
